import React, { ElementType, forwardRef } from "react"
import styled from "@emotion/styled"
import Box, { BoxProps } from "./Box"
import { variant } from "styled-system"
import { useTranslation } from "react-i18next"

export type TextsProps<T extends string> = {
  whiteSpace?: string
  as?: ElementType<any>
  variant?: T | T[]
} & BoxProps

export const textVariants = {
  h1: {
    fontSize: "64px",
    fontWeight: 600,
    lineHeight: "120%",
    letterSpacing: "-0.64px",
  },
  h1_m: {
    fontSize: "40px",
    fontWeight: 600,
    lineHeight: "120%",
    letterSpacing: "-0.4px",
  },
  h2: {
    fontSize: "48px",
    fontWeight: 600,
    lineHeight: "125%",
    letterSpacing: "0px",
  },
  h2_m: {
    fontSize: "32px",
    fontWeight: 600,
    lineHeight: "125%",
    letterSpacing: "0px",
  },
  h2_5: {
    fontSize: "36px",
    fontWeight: 600,
    lineHeight: "125%",
  },
  h3: {
    fontSize: "28px",
    fontWeight: 600,
    lineHeight: "140%",
    letterSpacing: "0px",
  },
  h3_m: {
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: "140%",
    letterSpacing: "0px",
  },
  h4: {
    fontSize: "20px",
    fontWeight: 600,
    lineHeight: "28px",
    letterSpacing: "0px",
  },
  h4_m: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "25.2px",
    letterSpacing: "0px",
  },
  h5: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "19px",
    letterSpacing: "0.05em",
  },
  h5_m: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "19px",
    letterSpacing: "0.03em",
  },
  t1_m: {
    //styleName: Mobile/T1;
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "23px",
  },
  t1b: {
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "155%",
  },
  t2: {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24.8px",
    letterSpacing: "0px",
  },
  t2_m: {
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "23.2px",
    letterSpacing: "0px",
  },
  t2_b: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "155%",
    letterSpacing: "0px",
  },
  t3: {
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "140%",
    letterSpacing: "0px",
  },
  t3_m: {
    //styleName: Mobile/T3;
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "140%",
    letterSpacing: "0px",
  },
  t4: {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "155%",
    letterSpacing: "0px",
  },
  t4_m: {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "19px",
    letterSpacing: "0px",
  },
  t5: {
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "155%",
    letterSpacing: "-0.11px",
  },
  t5_m: {
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "155%",
  },
  t1: {
    fontSize: "18px",
    fontWeight: 400,
    lineHeight: "155%",
  },
  l1: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "110%",
  },
  l1_m: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "110%",
  },
  l2: {
    fontSize: "16px",
    lineHeight: "110%",
    fontWeight: 600,
  },
  l2_m: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "110%",
  },
  l3: {
    fontSize: "14px",
    lineHeight: "110%",
    fontWeight: 600,
  },
  l3_m: { fontSize: "14px", lineHeight: "110%", fontWeight: 600 },
  caption_1: {
    fontSize: "12px",
    lineHeight: "133%",
    fontWeight: 500,
  },
  subhead_3: {
    fontSize: "14px",
    lineHeight: "100%",
    fontWeight: 600,
  },
  overline: {
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "16px",
  },
  body_1: {
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "20px",
  },
  body_2: {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "20px",
  },
  t1b_m: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "145%",
  },
  micro_text: {
    fontSize: "11px",
    fontWeight: 500,
    lineHeight: "16px",
  },
  label_small: {
    fontSize: "11px",
    fontWeight: 500,
    lineHeight: "16px",
  },
  tmgcs_title: {
    fontSize: "48px",
    fontWeight: 800,
    lineHeight: "120%",
    letterSpacing: "-0.48px",
  },
  tmgcs_title_tablet: {
    fontSize: "30px",
    fontWeight: 800,
    lineHeight: "120%",
    letterSpacing: "-0.3px",
  },
  tmgcs_title_mobile: {
    fontSize: "28px",
    fontWeight: 800,
    lineHeight: "120%",
    letterSpacing: "-0.28px",
  },
  default: {},
}

export interface TextProps<T extends string> extends BoxProps {
  whiteSpace?: string
  wordBreak?: string
  as?: ElementType<any>
  variant?: T | T[]
  fontSmoothing?: boolean
}

export const getWordBreak = (lang: string) => {
  switch (lang) {
    case "ko":
      return "keep-all"
    default:
      return "normal"
  }
}

const getTagFromVariant = (variant: string | string[] | undefined) => {
  if (variant === undefined) return "div"
  const variantWithoutSize = Array.isArray(variant)
    ? variant[0].split("_")[0]
    : variant.split("_")[0]

  switch (variantWithoutSize) {
    case "h1":
    case "h2":
    case "h3":
      return variantWithoutSize
    case "h4":
    case "h5":
      return variantWithoutSize
    default:
      return "div"
  }
}

const Text = styled(Box, { shouldForwardProp: prop => prop !== "fontSize" })<
  TextProps<keyof typeof textVariants>
>`
  word-break: ${({ whiteSpace }) => whiteSpace || "normal"};
  white-space: ${({ whiteSpace }) => whiteSpace || "pre-wrap"};
  ${() =>
    variant({
      variants: textVariants,
    })}
  ${({ fontSmoothing }) =>
    fontSmoothing &&
    `-webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;`}
`

const ForwardedText = forwardRef<
  HTMLAllCollection,
  TextProps<keyof typeof textVariants>
>(({ as, variant, dangerouslySetInnerHTML, ...props }, ref) => {
  const { i18n } = useTranslation()
  return (
    <Text
      as={as || getTagFromVariant(variant)}
      variant={variant}
      ref={ref}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      wordBreak={getWordBreak(i18n.language)}
      {...props}
    />
  )
})

ForwardedText.displayName = "Text"

export default ForwardedText
